import { AddressRecord } from "entities/CommunicationInfo";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "store/configureStore";
import { getCitiesByCounty, getCounties, getStates } from "store/slices/address";
import ProfileEditContentNode from "../ProfileEditContentNode";
import TypeaheadWithIcon from "pages/UI/TypeaheadWithIcon";
import { Form } from "react-bootstrap";

interface Props {
    currentAddress: AddressRecord;
    onAddressChange: (address: AddressRecord) => void;
}

const EditExistingAddressForm = ({ currentAddress, onAddressChange }: Props) => {
    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();

    const { countries, states, counties, cities } = useSelector((state: AppState) => state.entities.address);

    useEffect(() => {
        let hasUpdates = false;
        const updates: Partial<AddressRecord> = {};

        const countryObj = countries.list.find(c => c.Name === currentAddress.Country || c.Code === currentAddress.CountryCode);
        if (countryObj && !currentAddress.CountryId) {
            updates.CountryId = countryObj.Id;
            hasUpdates = true;

            dispatch(getStates(countryObj.Id));
            dispatch(getCounties(countryObj.Id));
        }

        if (currentAddress.State && states.list.length > 0 && !currentAddress.StateId) {
            const stateObj = states.list.find(s => s.Name === currentAddress.State);
            if (stateObj) {
                updates.StateId = stateObj.Id;
                hasUpdates = true;
            }
        }

        if (currentAddress.County && counties.list.length > 0 && !currentAddress.CountyId) {
            const countyObj = counties.list.find(c => c.Name === currentAddress.County);
            if (countyObj) {
                updates.CountyId = countyObj.Id;
                hasUpdates = true;
            }
        }

        if (hasUpdates) {
            const updatedAddress = { ...currentAddress, ...updates };

            const parts = [
                updatedAddress.Street,
                updatedAddress.City,
                updatedAddress.County,
                updatedAddress.State,
                updatedAddress.ZipCode
            ].filter(part => part && part.trim() !== "");
            updatedAddress.FullAddress = parts.join(", ");
            onAddressChange(updatedAddress);
        }
    }, [countries.list, states.list, counties.list, 
        currentAddress.Country, currentAddress.State, currentAddress.County, 
        currentAddress.CountryId, currentAddress.StateId, currentAddress.CountyId, dispatch]);

    const filteredCounties = useMemo(() => {
        if (!currentAddress.StateId) return counties.list;

        const firstCounty = counties.list[0];
        if (firstCounty && firstCounty.StateId === undefined) { 
            return counties.list;
        }
    
        return counties.list.filter(c => c.StateId === currentAddress.StateId);
    }, [counties.list, currentAddress.StateId]);

    const filteredCities = useMemo(() => {
        let list = cities.list;
        if (currentAddress.StateId) {
            list = list.filter(c => c.StateId === currentAddress.StateId);
        }
        if (currentAddress.CountyId) {
            list = list.filter(c => c.CountyId === currentAddress.CountyId);
        }
        return list;
    }, [cities.list, currentAddress.StateId, currentAddress.CountyId]);

    const handleUpdate = (updates: Partial<AddressRecord>) => {
        const updatedAddress = { ...currentAddress, ...updates };

        const parts = [
            updatedAddress.Street,
            updatedAddress.City,
            updatedAddress.County,
            updatedAddress.State,
            updatedAddress.ZipCode
        ].filter(part => part && part.trim() !== "");

        updatedAddress.FullAddress = parts.join(", ");
        
        onAddressChange(updatedAddress);
    };

    return (
        <div className="mt-3 edit-existing-address-form">

            {/* STATE */}
            <ProfileEditContentNode
                label={t("pages:profile.selectState")}
                mandatory
                handleClear={() => handleUpdate({ State: "", StateId: undefined, County: "", CountyId: undefined, City: "", CityId: undefined })}
                loading={states.loading}
            >
                <TypeaheadWithIcon
                    className="form-control-input-custom"
                    id="state-selection"
                    labelKey="Name"
                    options={states.list}
                    selected={states.list.filter(s => s.Name === currentAddress.State)}
                    onChange={(selected: any[]) => {
                        const state = selected[0];
                        if (!state) {
                            handleUpdate({ State: "", StateId: undefined, County: "", CountyId: undefined, City: "", CityId: undefined });
                            return;
                        }
                        handleUpdate({ 
                            State: state?.Name || "", 
                            StateId: state?.Id,
                            County: "",
                            CountyId: undefined,
                            City: "",
                            CityId: undefined
                        });
                        if (state?.Id) dispatch(getCounties(currentAddress.CountryId!, state.Id));
                    }}
                />
            </ProfileEditContentNode>

            {/* COUNTY */}
            <ProfileEditContentNode
                label={t("pages:profile.selectCounty")}
                mandatory
                handleClear={() => handleUpdate({ County: "", CountyId: undefined, City: "", CityId: undefined })}
                loading={counties.loading}
            >
                <TypeaheadWithIcon
                    className="form-control-input-custom"
                    id="county-selection"
                    labelKey="Name"
                    options={filteredCounties}
                    selected={filteredCounties.filter(c => c.Name === currentAddress.County)}
                    onChange={(selected: any[]) => {
                        const county = selected[0];
                        if (!county) {
                            handleUpdate({ County: "", CountyId: undefined, City: "", CityId: undefined });
                            return;
                        }

                        handleUpdate({ 
                            County: county?.Name || "", 
                            CountyId: county?.Id,
                            City: "",
                            CityId: undefined
                        });
                        if (county?.Id) dispatch(getCitiesByCounty(county.Id));
                    }}
                />
            </ProfileEditContentNode>

            {/* CITY */}
            <ProfileEditContentNode
                label={t("pages:profile.selectCity2")}
                mandatory
                handleClear={() => handleUpdate({ City: "", CityId: undefined })}
                loading={cities.loading}
            >
                <TypeaheadWithIcon
                    className="form-control-input-custom"
                    id="city-selection"
                    labelKey="Name"
                    allowNew
                    newSelectionPrefix={"+ "}
                    options={filteredCities}
                    selected={currentAddress.City ? [{ Name: currentAddress.City }] : []}
                    onInputChange={(text) => handleUpdate({City: text, CityId: undefined})}
                    onChange={(selected: any[]) => {
                        const selection = selected[0];
                        if (!selection) {
                            handleUpdate({ City: "", CityId: undefined });
                            return;
                        }

                        const cityName = selection.Name;
                        const cityId = selection.customOption ? undefined : selection.Id;

                        handleUpdate({ 
                            City: cityName, 
                            CityId: cityId
                        });
                    }}
                />
            </ProfileEditContentNode>

            {/* STREET */}
            <Form.Group className="mb-3" controlId="street-input">
                <Form.Label className="dataNode-label">{t("pages:profile.street")}</Form.Label>
                <Form.Control 
                    type="text"
                    className="form-control-input-custom" 
                    value={currentAddress.Street || ""} 
                    onChange={(e) => handleUpdate({ Street: e.target.value })} 
                    placeholder={t("common:form.placeholders.enterData")}
                />
            </Form.Group>

            {/* ZIP CODE */}
            <Form.Group className="mb-3" controlId="zip-code-input">
                <Form.Label className="dataNode-label">{t("pages:profile.zipCode")}</Form.Label>
                <Form.Control 
                    type="text" 
                    className="form-control-input-custom"
                    value={currentAddress.ZipCode || ""} 
                    onChange={(e) => handleUpdate({ ZipCode: e.target.value })} 
                    placeholder={t("common:form.placeholders.enterData")}
                />
            </Form.Group>
        </div>
    );
};

export default EditExistingAddressForm;